import { useRouter } from "next/router";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CoursesDropdown from "newComponents/Shared/_Atoms/CoursesDropdown/CoursesDropdown";
import { setActiveClassroom } from "Store/classrooms/actions";
import {
  handleFetchCourses,
  handleFetchLessonsFilterItems,
  handleFetchSubjects,
  handleFetchWorksheetsFilterItems,
  setActiveCourse
} from "Store/global/actions";
import { makeSelectActiveCourse, makeSelectCourses } from "Store/global/selectors";
import { makeSelectCurrentUser } from "Store/user/selectors";

import { handleFetchCourseTemplates } from "Store/courseTemplates/actions";
import { fetchInstructorSummary, handleFetchInstructors } from "Store/instructors/actions";
import { makeSelectCurrentInstructor } from "Store/instructors/selectors";
import { fetchActiveLocationSummary, handleFetchLocations } from "Store/locations/actions";
import { makeSelectActiveLocation } from "Store/locations/selectors";
import { getActiveCourseId, setActiveCourseId } from "utils/ActiveCourseService";

interface IProps {
  customClass?: string;
}

const CourseSelectDropDown: FC<IProps> = ({ customClass }) => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [coursesOptions, setCoursesOptions] = useState([]);
  const allCourses = useSelector(makeSelectCourses);
  const activeCourse = useSelector(makeSelectActiveCourse);
  const currentUser = useSelector(makeSelectCurrentUser);
  const currentInstructor = useSelector(makeSelectCurrentInstructor);
  const activeLocation = useSelector(makeSelectActiveLocation);
  const dispatch = useDispatch();
  const onSetActiveCourse = (course: Course) => dispatch(setActiveCourse(course));
  const onSetActiveClass = (selectedClass: Classroom | null) =>
    dispatch(setActiveClassroom(selectedClass));
  const onHandleFetchSubjects = () => dispatch(handleFetchSubjects());

  const router = useRouter();
  const linkOrigin = router.pathname;

  useEffect(() => {
    if (!coursesOptions?.length) {
      dispatch(handleFetchCourses());
    }
  }, []);

  useEffect(() => {
    if (allCourses && allCourses?.length) {
      let options: SelectOption[] = allCourses?.map((course) => ({
        label: course.course_name,
        value: course.course_id
      }));
      if (linkOrigin !== "/students" && linkOrigin !== "/temp") {
        options = options?.filter((option) => option.value !== "all_courses");
      }

      setCoursesOptions(options);
      const activeCourseId = getActiveCourseId();
      if (
        !activeCourseId ||
        (activeCourseId && activeCourseId === "all_courses" && linkOrigin !== "/students")
      ) {
        setSelectedCourse(options[0]);
        onSetActiveCourse(allCourses[0]);
        setActiveCourseId(allCourses.find((course) => course.course_name !== "All").course_id);
        onHandleFetchSubjects();
      } else {
        setSelectedCourse(options.find((option) => option.value === activeCourseId));
      }
    }
  }, [allCourses, linkOrigin]);

  useEffect(() => {
    if (activeCourse) {
      setSelectedCourse({
        label: activeCourse.course_name,
        value: activeCourse.course_id
      });
      dispatch(handleFetchLessonsFilterItems());
      dispatch(handleFetchWorksheetsFilterItems());
    }
  }, [activeCourse]);

  const handleSetActiveCourse = (course): void => {
    onSetActiveCourse(course);
    onHandleFetchSubjects();
  };

  const handleChangeCourse = async (event): Promise<void> => {
    const selectedCourseId = event.value;
    if (selectedCourseId && selectedCourseId !== selectedCourse.value) {
      await setActiveCourseId(
        selectedCourseId === "all_courses"
          ? allCourses.find((course) => course.course_name !== "All").course_id
          : selectedCourseId
      );
      setSelectedCourse(event);

      onConfirmCourseSelection(event.value);
    }
  };

  const onConfirmCourseSelection = (id) => {
    const linkOrigin = router.route;
    handleSetActiveCourse(allCourses?.find((course) => course.course_id === id));

    if (selectedCourse?.id === "all_courses") {
      return router.push("/students");
    }

    switch (linkOrigin) {
      case "/dashboard":
        return dispatch(fetchInstructorSummary(currentUser?.id));
      case "/students":
        return router.push("/students");
      case "/students/[studentId]":
        return router.push("/students");
      case "/instructors":
        return dispatch(handleFetchInstructors());
      case "/instructors/[instructorId]":
        return dispatch(fetchInstructorSummary(currentInstructor?.id));
      case "/classrooms":
        return onSetActiveClass(null);
      case "/classrooms/[classId]":
        onSetActiveClass(null);
        return router.push("/classrooms");
      case "/locations":
        return dispatch(handleFetchLocations());
      case "/locations/[locationId]":
        if (activeLocation.id) {
          return dispatch(fetchActiveLocationSummary(activeLocation?.id));
        }
        return null;
      case "/courses":
        return dispatch(handleFetchCourseTemplates());
      default:
    }
  };

  return (
    <CoursesDropdown
      value={coursesOptions.find((option) => option.value === activeCourse.course_id)}
      options={coursesOptions}
      label="View"
      onChange={(event) => handleChangeCourse(event)}
      customClass={customClass}
    />
  );
};

export default CourseSelectDropDown;
